import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import DisplayDate from '../../components/DisplayDate';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import Button from '../../components/Button';
import StatusIcon from '../../components/StatusIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import NoInfoMessage from '../../components/NoInfoMessage';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { PolicyTemplateDashboardPolicyTemplate, PolicyTemplateReorderCommand } from '../../api/models';

const ContentPolicyTemplates: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Template Id
	const [templateId, setTemplateId] = React.useState<string>('');

	// State to store Child Safe Templates title
	const [templateTitle, setTemplateTitle] = React.useState<string>('');

	// Set up Data state
	const [policyTemplatesData, setPolicyTemplatesData] = React.useState<PolicyTemplateDashboardPolicyTemplate[]>([]);

	const getPolicyTemplatesData = async () => {
		setIsLoading(true);

		const response = await apibridge.getPolicyTemplateDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setPolicyTemplatesData(result);
		}
	};

	React.useEffect(() => {
		getPolicyTemplatesData();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(policyTemplatesData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setPolicyTemplatesData(items);

		const data: PolicyTemplateReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postPolicyTemplateReorder(data);
	};

	const removeHandler = async (item: PolicyTemplateDashboardPolicyTemplate) => {
		setTemplateId(item.id || '');

		setTemplateTitle(item.title || '');

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="/content-policy-templates-edit">
										<PlusIcon className="icon-link" /> Add new
									</Link>
								</div>

								<section className="info-section mtop-0">
									{policyTemplatesData && policyTemplatesData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="trainingModulesItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{policyTemplatesData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row">
																				<div className="col-auto pdl-0 pdr-0">
																					<StatusIcon statusType="read" />
																				</div>

																				<div className="col-md-auto full-width">
																					<div className="row detail-info">
																						<div className="col-10 col-md-6 detail-name">{item.title}</div>

																						<div className="col-10 col-md-6 text-category-detail">
																							Last updated &nbsp; &nbsp;
																							<span className="text-subtitle">
																								<DisplayDate>{item.updated}</DisplayDate>
																							</span>
																						</div>
																					</div>

																					<div className="d-block d-sm-none policy-templates">
																						<a className="btn-text remove link" download href={item.attachmentUrl}>
																							Download
																						</a>

																						<Link
																							className="btn-text remove link"
																							to={`/content-policy-templates-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="col-auto pdr-0 link-group content-training-module-page">
																					<div className="d-none d-sm-flex">
																						<a className="btn-text remove link" download href={item.attachmentUrl}>
																							Download
																						</a>

																						<Link
																							className="btn-text remove link"
																							to={`/content-policy-templates-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>

																					<div className="drag-icon-container">
																						<DisplayDragIcon index={index} array={policyTemplatesData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<NoInfoMessage name="Policy templates" />
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove template">
				<RemoveModal
					policyTemplatesData={policyTemplatesData}
					setPolicyTemplatesData={setPolicyTemplatesData}
					getPolicyTemplatesData={getPolicyTemplatesData}
					templateId={templateId}
					templateTitle={templateTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentPolicyTemplates;
